import { getErrorMessage } from '@features/shared/api/client'
import { useEffect, useRef, useState, type ClipboardEvent, type DragEvent } from 'react'
import { useLocation } from 'react-router-dom'
import { getAppModuleIdForPath } from '@/config/appNavigation'
import { useAppNavigation } from '@features/navigation/hooks/useAppNavigation'
import { DisabledReason } from '@features/shared/components/DisabledReason'
import { FieldInput } from '@features/shared/components/FieldInput'
import { ToggleSwitch } from '@features/shared/components/ToggleSwitch'
import { CheckIcon } from '@features/shared/icons/icons'
import { useSubmitSupportRequest } from '../hooks/useSubmitSupportRequest'
import { useSupportAttachments } from '../hooks/useSupportAttachments'
import { useSupportCategories } from '../hooks/useSupportCategories'
import SidePanelShell from './SidePanelShell'
import SupportAttachmentsField from './SupportAttachmentsField'

interface SupportPanelProps {
  onClose: () => void
  /** Showing: it stays mounted, hidden, while another panel takes its place. */
  open: boolean
}

const SUPPORT_PANEL_MIN_WIDTH_PX = 320
const SUPPORT_PANEL_MAX_WIDTH_PX = 480

// Mirror the backend's limits (app/support/schema.py).
const SUBJECT_MAX_LENGTH = 200
const DESCRIPTION_MAX_LENGTH = 5000

/**
 * The module a request is about: the one the user is in (it follows them while
 * the panel stays open), or one they picked, "No specific module" included.
 */
type ReportedModule = { kind: 'current' } | { kind: 'picked'; moduleId: string }

/** The Module field's choice for no one module, sent as null. */
const NO_SPECIFIC_MODULE = '__none__'
/** Signing in isn't on the rail, but trouble with it is a request's module. */
const AUTHENTICATION_MODULE = { value: 'authentication', label: 'Authentication' }

const PROMPTS: Record<string, { subject: string; description: string }> = {
  support: {
    subject: 'e.g. How do I share a view?',
    description: 'What are you trying to do?',
  },
  bug: {
    subject: "e.g. Can't open a table",
    description: 'What happened? Steps to reproduce, if any.',
  },
  feature: {
    subject: 'e.g. Export a view to PDF',
    description: 'What would you like to do, and why?',
  },
  feedback: {
    subject: 'e.g. The new sidebar',
    description: "What's on your mind?",
  },
}
const DEFAULT_PROMPT = { subject: 'A short summary', description: 'How can we help?' }

const PRIVATE_LABEL = 'Keep private from my organization'

function hasFiles(event: DragEvent): boolean {
  return Array.from(event.dataTransfer?.types ?? []).includes('Files')
}

export default function SupportPanel({ onClose, open }: SupportPanelProps) {
  const location = useLocation()
  const { modules } = useAppNavigation()
  const {
    categories,
    isLoading: categoriesLoading,
    isError: categoriesError,
  } = useSupportCategories()
  const [categoryId, setCategoryId] = useState('')
  const [reportedModule, setReportedModule] = useState<ReportedModule>({ kind: 'current' })
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [isPrivate, setIsPrivate] = useState(false)
  const { submit, isSubmitting, isSuccess, reset } = useSubmitSupportRequest()
  const [error, setError] = useState<string | null>(null)
  const files = useSupportAttachments()
  const [dragging, setDragging] = useState(false)

  // Opened with nothing chosen, it starts on its first question, the choices
  // showing; reopened mid-request, it leaves the draft as it was. Decided
  // once each time it opens, once the choices are in.
  const [questionOpenRequest, setQuestionOpenRequest] = useState(0)
  const decidedOnOpenRef = useRef(false)
  useEffect(() => {
    if (!open) {
      decidedOnOpenRef.current = false
      return
    }
    if (decidedOnOpenRef.current || categoriesLoading) return
    decidedOnOpenRef.current = true
    if (categoryId === '' && categories.length > 0) setQuestionOpenRequest((count) => count + 1)
  }, [open, categoriesLoading, categoryId, categories.length])

  const category = categories.find((candidate) => candidate.category_id === categoryId)
  const prompt = (category && PROMPTS[category.slug]) ?? DEFAULT_PROMPT
  // The rail's modules, in its order, after the stand-in; then signing in.
  const moduleOptions = [
    { value: NO_SPECIFIC_MODULE, label: 'No specific module', italic: true },
    ...modules.map((module) => ({ value: module.id, label: module.label })),
    AUTHENTICATION_MODULE,
  ]
  const currentModuleId = getAppModuleIdForPath(location.pathname)
  const reportedModuleId =
    reportedModule.kind === 'picked'
      ? reportedModule.moduleId
      : modules.some((module) => module.id === currentModuleId) && currentModuleId
        ? currentModuleId
        : NO_SPECIFIC_MODULE

  // Anything entered since the panel opened or the last request was sent.
  const hasDraft =
    categoryId !== '' ||
    reportedModule.kind !== 'current' ||
    subject !== '' ||
    description !== '' ||
    isPrivate ||
    files.items.length > 0
  const clearFields = () => {
    setCategoryId('')
    setReportedModule({ kind: 'current' })
    setSubject('')
    setDescription('')
    setIsPrivate(false)
  }

  // Why Submit is unavailable, or null once the request can be sent.
  const submitBlocker = !category
    ? 'Choose what you would like to do'
    : !subject.trim()
      ? 'Add a subject'
      : !description.trim()
        ? 'Add a description'
        : subject.length > SUBJECT_MAX_LENGTH
          ? `Keep the subject to ${SUBJECT_MAX_LENGTH} characters`
          : description.length > DESCRIPTION_MAX_LENGTH
            ? `Keep the description to ${DESCRIPTION_MAX_LENGTH.toLocaleString()} characters`
            : files.isUploading
              ? 'Wait for the files to finish uploading'
              : files.hasErrors
                ? "Remove the files that couldn't be attached"
                : null

  const handleSubmit = async () => {
    setError(null)
    try {
      await submit({
        category_id: categoryId,
        subject: subject.trim(),
        description: description.trim(),
        // The exact page goes along, whatever module the request names.
        submission_url: window.location.href,
        reported_module: reportedModuleId === NO_SPECIFIC_MODULE ? null : reportedModuleId,
        is_private: isPrivate,
        attachment_ids: files.ready.map((attachment) => attachment.attachment_id),
      })
      clearFields()
      // Sent: the files are the request's now, so none is deleted.
      files.clear()
    } catch (err) {
      setError(getErrorMessage(err, "This support request couldn't be sent. Try again."))
    }
  }

  // Files dropped anywhere on the form, or pasted into it, are attached,
  // once it's asking for the request's details.
  const acceptsFiles = files.available && Boolean(category) && !isSubmitting
  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    const pasted = Array.from(event.clipboardData?.files ?? [])
    if (!acceptsFiles || pasted.length === 0) return
    event.preventDefault()
    files.addFiles(pasted)
  }
  const dragHandlers = acceptsFiles
    ? {
        onDragEnter: (event: DragEvent<HTMLDivElement>) => {
          if (!hasFiles(event)) return
          event.preventDefault()
          setDragging(true)
        },
        onDragOver: (event: DragEvent<HTMLDivElement>) => {
          if (!hasFiles(event)) return
          event.preventDefault()
          event.dataTransfer.dropEffect = 'copy'
        },
        onDragLeave: (event: DragEvent<HTMLDivElement>) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false)
        },
        onDrop: (event: DragEvent<HTMLDivElement>) => {
          if (!hasFiles(event)) return
          event.preventDefault()
          setDragging(false)
          files.addFiles(Array.from(event.dataTransfer.files))
        },
      }
    : {}

  return (
    <SidePanelShell
      title="Contact Support"
      subtitle="Our support team will follow up by email."
      onClose={onClose}
      minWidthPx={SUPPORT_PANEL_MIN_WIDTH_PX}
      maxWidthPx={SUPPORT_PANEL_MAX_WIDTH_PX}
      bodyClassName="sidebar-scrollbar flex-1 overflow-y-auto px-5 py-4"
    >
      {isSuccess ? (
        <div className="flex flex-col items-center gap-3 py-10 text-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-full tone-green bg-[color:var(--tone-bg-subtle)] text-[color:var(--tone-icon)]">
            <CheckIcon size={18} />
          </div>
          <p className="text-sm font-medium text-default">Request sent</p>
          <p className="text-xs text-muted">We got your message and will follow up by email.</p>
          <button
            type="button"
            onClick={() => reset()}
            className="mt-1 text-xs font-medium text-[color:var(--text-info)] hover:underline"
          >
            Send another request
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3" onPaste={handlePaste} {...dragHandlers}>
          <FieldInput
            type="select"
            label="What would you like to do?"
            value={categoryId}
            onChange={setCategoryId}
            options={categories.map((option) => ({
              value: option.category_id,
              label: option.label,
            }))}
            placeholder={categoriesLoading ? 'Loading…' : 'Choose one'}
            openRequest={questionOpenRequest}
            required
            error={categoriesError}
            errorMessage={
              categoriesError ? "Support options couldn't be loaded. Try again later." : undefined
            }
          />
          {/* Each question appears once the one before it is answered; the
              module starts as the one the user is in, so the rest follow at once. */}
          {category ? (
            <>
              {/* Always answered: "No specific module" is an answer. */}
              <FieldInput
                type="select"
                label="Module"
                value={reportedModuleId}
                onChange={(moduleId) => setReportedModule({ kind: 'picked', moduleId })}
                options={moduleOptions}
                required
              />
              <FieldInput
                type="short-text"
                label="Subject"
                value={subject}
                onChange={setSubject}
                placeholder={prompt.subject}
                required
              />
              <FieldInput
                type="long-text"
                label="Description"
                value={description}
                onChange={setDescription}
                placeholder={prompt.description}
                required
              />
              {files.available ? (
                <SupportAttachmentsField
                  files={files}
                  dragging={dragging}
                  disabled={isSubmitting}
                />
              ) : null}
              {/* A quiet aside, not a question: one line at the end. The
                  words switch it too. */}
              <label className="flex cursor-pointer items-center justify-end gap-2 whitespace-nowrap text-xs text-muted">
                {PRIVATE_LABEL}
                <ToggleSwitch
                  checked={isPrivate}
                  onChange={setIsPrivate}
                  ariaLabel={PRIVATE_LABEL}
                />
              </label>
              {error ? <p className="text-xs text-danger">{error}</p> : null}
            </>
          ) : null}
          {/* As the app's other forms end: Cancel closes the panel, which
              clears the draft; Discard clears it and stays; Submit sends. */}
          <div className="mt-1 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md bg-muted px-3 py-1.5 text-xs font-medium text-subtle transition-colors hover:bg-[color:var(--bg-emphasis)] hover:text-default"
            >
              Cancel
            </button>
            <DisabledReason reason={hasDraft || isSubmitting ? null : 'Nothing to discard'}>
              <button
                type="button"
                onClick={() => {
                  clearFields()
                  // Discarded: the uploads go too.
                  files.removeAll()
                  setError(null)
                }}
                disabled={!hasDraft || isSubmitting}
                className="rounded-md bg-muted px-3 py-1.5 text-xs font-medium text-subtle transition-colors enabled:hover:bg-[color:var(--bg-emphasis)] enabled:hover:text-default disabled:cursor-not-allowed disabled:opacity-50"
              >
                Discard
              </button>
            </DisabledReason>
            <DisabledReason reason={isSubmitting ? null : submitBlocker} align="right">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting || submitBlocker !== null}
                className="rounded-md bg-[color:var(--action-brand-bg)] px-3 py-1.5 text-xs font-medium text-[color:var(--action-brand-text)] transition-colors hover:bg-[color:var(--action-brand-bg-hover)] disabled:cursor-not-allowed disabled:bg-[color:var(--border-strong)] disabled:opacity-60"
              >
                {isSubmitting ? 'Submitting…' : 'Submit'}
              </button>
            </DisabledReason>
          </div>
        </div>
      )}
    </SidePanelShell>
  )
}
